import React from 'react';
import { Table, Button, Tag, Badge, Tooltip, Typography } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { VulnerabilidadeCSA } from '../../../types/dashboard/csa.types';

const { Text } = Typography;

interface CSAVulnerabilityTableProps {
  data: VulnerabilidadeCSA[];
  loading: boolean;
  pagination: {
    current: number;
    pageSize: number;
    total: number;
  };
  onTableChange: (pagination: any, filters: any, sorter: any) => void;
  onViewDetails: (record: VulnerabilidadeCSA) => void;
}

const CSAVulnerabilityTable: React.FC<CSAVulnerabilityTableProps> = ({
  data,
  loading,
  pagination,
  onTableChange,
  onViewDetails
}) => {
  /**
   * Tratar valores null, vazios ou inválidos
   */
  const padronizarCVE = (cve: string): { cve: string; isPendente: boolean } => {
    if (!cve || cve.trim() === '' || cve.toLowerCase() === 'null' || cve === 'null') {
      return { cve: 'CVE PENDENTE', isPendente: true };
    }
    return { cve: cve.trim(), isPendente: false };
  };

  /**
   * Tratar valores Unknown
   */
  const padronizarHost = (host: string, ip: string): { host: string; isUnknown: boolean } => {
    if (!host || host.trim() === '' || host.toLowerCase() === 'unknown') {
      const hostPadronizado = ip ? `Host-${ip}` : 'Host Desconhecido';
      return { host: hostPadronizado, isUnknown: true };
    }
    return { host: host.trim(), isUnknown: false };
  };

  const columns: ColumnsType<VulnerabilidadeCSA> = [
    {
      title: 'CVE',
      dataIndex: 'cve',
      key: 'cve',
      width: 140,
      sorter: true,
      render: (cve: string) => {
        const { cve: cvePadronizado, isPendente } = padronizarCVE(cve);
        
        return (
          <Text 
            code 
            copyable={{ text: cvePadronizado }}
            style={{ 
              color: isPendente ? '#faad14' : undefined,
              fontStyle: isPendente ? 'italic' : undefined
            }}
            title={isPendente ? 'CVE ainda não identificado' : undefined}
          >
            {cvePadronizado}
          </Text>
        );
      }
    },
    {
      title: 'Host',
      dataIndex: 'host',
      key: 'host',
      width: 200,
      sorter: true,
      render: (host: string, record: VulnerabilidadeCSA) => {
        const { host: hostPadronizado, isUnknown } = padronizarHost(host, record.ip);
        
        return (
          <Tooltip title={isUnknown ? `Host não identificado - IP: ${record.ip}` : hostPadronizado}>
            <Text 
              ellipsis 
              style={{ 
                maxWidth: 180,
                color: isUnknown ? '#ff7a45' : undefined,
                fontStyle: isUnknown ? 'italic' : undefined
              }}
            >
              {hostPadronizado}
            </Text>
          </Tooltip>
        );
      }
    },
    {
      title: 'IP',
      dataIndex: 'ip',
      key: 'ip',
      width: 120,
      render: (ip: string) => (
        <Text code copyable={{ text: ip }}>
          {ip}
        </Text>
      )
    },
    {
      title: 'Severidade',
      dataIndex: 'severity',
      key: 'severity',
      width: 120,
      sorter: true,
      render: (severity: string, record: VulnerabilidadeCSA) => (
        <Tag color={record.severityInfo.color}>
          {record.severityInfo.label}
        </Tag>
      )
    },
    {
      title: 'Score CVSS',
      dataIndex: 'cvssScore',
      key: 'cvssScore',
      width: 100,
      sorter: true,
      render: (score: string, record: VulnerabilidadeCSA) => (
        <Badge
          count={score}
          style={{ backgroundColor: record.riscoGeral.cor }}
          showZero
          title={`Score CVSS: ${score} (${record.riscoGeral.nivel})`}
        />
      )
    },
    {
      title: 'Porta',
      dataIndex: 'port',
      key: 'port',
      width: 80,
      render: (port: string) => (
        <Text code>{port}</Text>
      )
    },
    {
      title: 'Data do CSA',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 120,
      sorter: true,
      render: (date: string) => (
        <Tooltip title={`CSA realizado em: ${dayjs(date).format('DD/MM/YYYY HH:mm:ss')}`}>
          <Text>{dayjs(date).fromNow()}</Text>
        </Tooltip>
      )
    },
    {
      title: 'Ações',
      key: 'actions',
      width: 80,
      fixed: 'right',
      render: (record: VulnerabilidadeCSA) => (
        <Button
          type="primary"
          icon={<EyeOutlined />}
          onClick={() => onViewDetails(record)}
          size="small"
        >
          Visualizar
        </Button>
      )
    }
  ];

  return (
    <Table
      columns={columns}
      dataSource={data}
      loading={loading}
      rowKey="id"
      pagination={{
        current: pagination.current,
        pageSize: pagination.pageSize,
        total: pagination.total,
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (total, range) => 
          `${range[0]}-${range[1]} de ${total} vulnerabilidades`,
        pageSizeOptions: ['10', '20', '50', '100']
      }}
      onChange={onTableChange}
      scroll={{ x: 1000 }}
      size="small"
    />
  );
};

export default CSAVulnerabilityTable;